import { normalizeDocument } from "./documents";
import { avgTicket, dreValues, kpi, ratioPct4, sumOrNull, type Kpi } from "./dashboard";

/**
 * Multiempresa avançada (T3.10): consolidação dos dashboards entre as empresas do tenant. Toda soma entre empresas é
 * em BigInt (via `sumOrNull`), com null quando passa de 2^53 ou quando uma parte é desconhecida. O ERP só grava
 * valores em BRL (pedido de canal em outra moeda vai para revisão), então nada aqui converte moeda.
 */

/** Empresas numa consolidação (seleção da tela) e por conta. */
export const MAX_CONSOLIDATED_COMPANIES = 20;
export const MAX_TENANT_COMPANIES = 50;
/** `eliminate` (padrão) tira do total as operações entre empresas da consolidação; `include` soma tudo. */
export const INTERCOMPANY_MODES = ["eliminate", "include"] as const;
export type IntercompanyMode = (typeof INTERCOMPANY_MODES)[number];
export const CONSOLIDATION_CURRENCY = "BRL" as const;

// ---------- intercompanhia ----------

const cnpjOf = (doc: string | null | undefined): string | null => {
  if (!doc) return null;
  const d = normalizeDocument(doc);
  return /^[0-9A-Z]{12}\d{2}$/.test(d) ? d : null;
};

/** Raiz do CNPJ (8 primeiros caracteres: a mesma para matriz e filiais); CPF ou vazio = null. */
export const groupCnpjRoot = (doc: string | null | undefined): string | null =>
  cnpjOf(doc)?.slice(0, 8) ?? null;

export type GroupCompany = { id: string; cnpj: string | null };

/**
 * Empresa da consolidação que é a contraparte de uma venda/conta da empresa `sellerId` com o contato de documento
 * `document`: a de CNPJ igual; senão a primeira de mesma raiz (filial não cadastrada como empresa). A própria vendedora
 * e as filiais dela (mesma raiz) não contam; empresa fora da lista é cliente externo.
 */
export function intercompanyCounterpart(
  companies: readonly GroupCompany[],
  sellerId: string,
  document: string | null | undefined,
): string | null {
  const doc = cnpjOf(document);
  if (!doc) return null;
  const seller = companies.find((c) => c.id === sellerId);
  const sellerCnpj = cnpjOf(seller?.cnpj);
  if (doc === sellerCnpj) return null;
  const others = companies.filter((c) => c.id !== sellerId);
  const exact = others.find((c) => cnpjOf(c.cnpj) === doc);
  if (exact) return exact.id;
  const root = doc.slice(0, 8);
  if (root === sellerCnpj?.slice(0, 8)) return null;
  return others.find((c) => groupCnpjRoot(c.cnpj) === root)?.id ?? null;
}

/**
 * O que procurar nos contatos da empresa `sellerId` (índice por documento): os CNPJs das outras empresas e as raízes
 * delas que não são a raiz da própria vendedora. A decisão final é sempre de `intercompanyCounterpart`.
 */
export function groupDocuments(
  companies: readonly GroupCompany[],
  sellerId: string,
): { exact: string[]; roots: string[] } {
  const sellerRoot = groupCnpjRoot(companies.find((c) => c.id === sellerId)?.cnpj);
  const exact = new Set<string>();
  const roots = new Set<string>();
  for (const c of companies) {
    if (c.id === sellerId) continue;
    const cnpj = cnpjOf(c.cnpj);
    if (!cnpj) continue;
    exact.add(cnpj);
    if (cnpj.slice(0, 8) !== sellerRoot) roots.add(cnpj.slice(0, 8));
  }
  return { exact: [...exact].sort(), roots: [...roots].sort() };
}

// ---------- somas ----------

/** As medidas de venda dos fatos (`sales_facts_daily`/`sales_fact_orders`). */
export type SalesMeasures = {
  orders: number | null;
  totalCents: number | null;
  freightCents: number | null;
  qtyQ4: number | null;
  costCents: number | null;
  costMissing: number | null;
};
const MEASURE_KEYS = ["orders", "totalCents", "freightCents", "qtyQ4", "costCents", "costMissing"] as const;
export const EMPTY_MEASURES: SalesMeasures = {
  orders: 0,
  totalCents: 0,
  freightCents: 0,
  qtyQ4: 0,
  costCents: 0,
  costMissing: 0,
};

export function sumMeasures(list: readonly SalesMeasures[]): SalesMeasures {
  const out = { ...EMPTY_MEASURES };
  for (const k of MEASURE_KEYS) out[k] = sumOrNull(list.map((x) => x[k]));
  return out;
}

/** a − b, medida a medida (a parte "para empresas do grupo" sai do bruto da empresa). */
export function subtractMeasures(a: SalesMeasures, b: SalesMeasures): SalesMeasures {
  const out = { ...EMPTY_MEASURES };
  for (const k of MEASURE_KEYS) {
    const v = b[k];
    out[k] = sumOrNull([a[k], v === null ? null : -v]);
  }
  return out;
}

/** Soma posição a posição (dia relativo); série mais curta conta zero nas posições que faltam. */
export function sumSeries(
  series: readonly (readonly (number | null)[])[],
  length: number,
): (number | null)[] {
  return Array.from({ length }, (_, i) => sumOrNull(series.map((s) => (i < s.length ? s[i]! : 0))));
}

/** Participação da parte no total (pct4); total ≤ 0 ou desconhecido = null. */
export const companySharePct4 = (part: number | null, total: number | null): number | null =>
  ratioPct4(part, total);

// ---------- cards e DRE ----------

export type GroupCards = { totalCents: Kpi; orders: Kpi; itemsQ4: Kpi; avgTicketCents: Kpi };

/** Cards do grupo: somas das partes (atual e comparação) com ticket e variação recalculados sobre as somas. */
export function groupCards(
  current: readonly SalesMeasures[],
  previous: readonly SalesMeasures[],
): GroupCards {
  const c = sumMeasures(current);
  const p = sumMeasures(previous);
  return {
    totalCents: kpi(c.totalCents, p.totalCents),
    orders: kpi(c.orders, p.orders),
    itemsQ4: kpi(c.qtyQ4, p.qtyQ4),
    avgTicketCents: kpi(avgTicket(c.totalCents, c.orders), avgTicket(p.totalCents, p.orders)),
  };
}

export type GroupDrePart = {
  current: SalesMeasures;
  previous: SalesMeasures;
  /** Configuração da empresa (RF-BI-14). */
  taxPct4: number;
  feePct4: number;
};

/** DRE do grupo; `taxPct4`/`salesFeePct4` são os efetivos (impostos ÷ faturamento), null sem faturamento. */
export type GroupDre = {
  netRevenueCents: Kpi;
  taxesCents: Kpi;
  feesCents: Kpi;
  taxPct4: number | null;
  salesFeePct4: number | null;
  costCents?: Kpi;
  grossMarginPct4?: Kpi;
  profitCents?: Kpi;
  profitPct4?: Kpi;
  costMissingOrders?: number | null;
};

type DreSums = { net: number | null; taxes: number | null; fees: number | null; cost: number | null };

function dreSums(parts: readonly GroupDrePart[], pick: "current" | "previous", withCost: boolean): DreSums {
  const rows = parts.map((p) =>
    dreValues({
      totalCents: p[pick].totalCents,
      freightCents: p[pick].freightCents,
      costCents: withCost ? p[pick].costCents : null,
      taxPct4: p.taxPct4,
      feePct4: p.feePct4,
    }),
  );
  return {
    net: sumOrNull(rows.map((r) => r.netRevenueCents)),
    taxes: sumOrNull(rows.map((r) => r.taxesCents)),
    fees: sumOrNull(rows.map((r) => r.feesCents)),
    cost: withCost ? sumOrNull(rows.map((r) => r.costCents)) : null,
  };
}

const neg = (v: number | null) => (v === null ? null : -v);

/**
 * DRE resumida do grupo: cada empresa com o próprio % de imposto e de taxa (sobre o faturamento sem frete dela), as
 * contas somadas e margem/lucro % recalculados sobre as somas. Custo, margem e lucro só com `withCost`.
 */
export function groupDre(parts: readonly GroupDrePart[], withCost: boolean): GroupDre {
  const a = dreSums(parts, "current", withCost);
  const b = dreSums(parts, "previous", withCost);
  const out: GroupDre = {
    netRevenueCents: kpi(a.net, b.net),
    taxesCents: kpi(a.taxes, b.taxes),
    feesCents: kpi(a.fees, b.fees),
    taxPct4: ratioPct4(a.taxes, a.net),
    salesFeePct4: ratioPct4(a.fees, a.net),
  };
  if (!withCost) return out;
  const gross = (s: DreSums) => sumOrNull([s.net, neg(s.cost)]);
  const profit = (s: DreSums) => sumOrNull([gross(s), neg(s.taxes), neg(s.fees)]);
  return {
    ...out,
    costCents: kpi(a.cost, b.cost),
    grossMarginPct4: kpi(ratioPct4(gross(a), a.net), ratioPct4(gross(b), b.net)),
    profitCents: kpi(profit(a), profit(b)),
    profitPct4: kpi(ratioPct4(profit(a), a.net), ratioPct4(profit(b), b.net)),
    costMissingOrders: sumOrNull(parts.map((p) => p.current.costMissing)),
  };
}

// ---------- financeiro ----------

/** Posição financeira de hoje; cada coluna só existe com a leitura do módulo dela. */
export type FinancePosition = {
  receivableOpenCents?: number | null;
  receivableOverdueCents?: number | null;
  payableOpenCents?: number | null;
  payableOverdueCents?: number | null;
  accountsBalanceCents?: number | null;
};
const FINANCE_KEYS = [
  "receivableOpenCents",
  "receivableOverdueCents",
  "payableOpenCents",
  "payableOverdueCents",
  "accountsBalanceCents",
] as const;

/** Total do grupo: só as colunas presentes em todas as empresas. */
export function groupFinance(parts: readonly FinancePosition[]): FinancePosition {
  const out: FinancePosition = {};
  if (parts.length === 0) return out;
  for (const k of FINANCE_KEYS) {
    if (parts.every((p) => k in p)) out[k] = sumOrNull(parts.map((p) => p[k] ?? null));
  }
  return out;
}
